import { createContext, FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import cookie from 'js-cookie';
import { ApolloError, useApolloClient, useQuery } from '@apollo/client';

import { getIntlValidLocale } from '@app/helpers/locale';
import useLocalizationCountry from '@app/hooks/useLocalizationCountry';
import { SHOP_STORE_CODE_QUERY } from './ShopStoreContext.queries';

import {
  ShopStoreCodeQuery,
  ShopStoreCodeQueryVariables,
} from './__generated__/ShopStoreCodeQuery';

export const SHOP_STORE_CODE_COOKIE = 'store-view-code';

export interface ShopStoreContextValue {
  storeCode?: string | null;
  storeId?: string | null;
  error?: ApolloError;
  loading: boolean;
}

const initialValue: ShopStoreContextValue = {
  loading: true,
};

export const ShopStoreContext = createContext<ShopStoreContextValue>(
  initialValue
);

export const ShopStoreProvider: FC = ({ children }) => {
  /*
  Using this proxy state to control when child components update.
  Writing cookies in a useEffect or in the useQuery#onCompleted callback while passing on query values directly does not guarantee the cookies are set before child components update. in that case subsequent requests can occur without the Store header properly set with the cookie value. using this proxy value allows us to write the cookie, then update children based on state change.
  */
  const [value, setValue] = useState<ShopStoreContextValue>(initialValue);
  const { language = 'en' } = useParams<{ language?: string }>();
  const locale = getFullShopLocaleFromRouteParam(language);
  const { cache } = useApolloClient();
  const { country, loading: countryLoading } = useLocalizationCountry();

  const { error, loading: codeLoading, data: codeData } = useQuery<
    ShopStoreCodeQuery,
    ShopStoreCodeQueryVariables
  >(SHOP_STORE_CODE_QUERY, {
    skip: countryLoading,
    variables: { country: country || 'US', language: locale },
    fetchPolicy: 'cache-first',
  });

  const loading = countryLoading || codeLoading;
  const [store] = codeData?.magento2?.countryStoreCode?.allStores ?? [];
  const { storeCode, storeId } = store ?? {};

  useEffect(() => {
    if (loading || error) {
      setValue((current) => ({ ...current, loading, error }));

      return;
    }

    if (!storeCode) {
      cookie.remove(SHOP_STORE_CODE_COOKIE);
      throw new Error(`No shop store found for ${country}, ${locale}`);
    }

    const stored = cookie.get(SHOP_STORE_CODE_COOKIE);

    if (storeCode !== stored) {
      // Make sure cookie is set before children respond to updated context value
      cookie.set(SHOP_STORE_CODE_COOKIE, storeCode);
    }

    setValue((current) => ({
      ...current,
      loading,
      error,
      storeCode,
      storeId,
    }));
  }, [country, error, loading, locale, storeCode, storeId, cache]);

  return (
    // use key to force child re-render, some queries don't refetch
    // on cache-eviction
    <ShopStoreContext.Provider value={value} key={storeCode}>
      {children}
    </ShopStoreContext.Provider>
  );
};

const localeDefaultsMap = {
  de: 'de_DE',
  en: 'en_US',
  es: 'es_ES',
  fr: 'fr_FR',
  ja: 'ja_JP',
  zh: 'zh_Hans_CN',
};

/*
Using the country can lead to locales that are invalid for our stores
eg. we don't have any GB stores for en_GB, which leads to a empty store code, while do have a GB store with en_US that should be used.
*/
export const getFullShopLocaleFromRouteParam = (language: string): string =>
  localeDefaultsMap[getIntlValidLocale(language.toLowerCase())];
